/* src/core/types.ts — shared client types, and the money boundary.
 *
 * The full `GameState` (all 39 observed top-level keys, PLAN.md Part 1 §4) is typed in
 * TASKS.md 2.4. What lives here in Phase 0 is the part the network boundary needs on day
 * one: the branded `Money` type, so a currency value can never be confused with a plain
 * number anywhere downstream.
 */

/**
 * Money is a brand over `number`. Server-side it is `numeric(14,4)`; on the wire it is a
 * string (PLAN.md Part 3 §7) so it never round-trips through a float in transit. The brand
 * makes `cash = 5` a type error, which is the point — CLAUDE.md's rule is that the client
 * never derives a currency value from anything but a server response, and an unbranded
 * number is exactly how that rule gets broken by accident.
 */
export type Money = number & { readonly __brand: 'Money' };

/** The single parse point. Called at the net/apiClient.ts boundary, nowhere else. */
export function parseMoney(raw: string | number): Money {
  const n = typeof raw === 'string' ? Number(raw) : raw;
  if (!Number.isFinite(n)) throw new Error(`parseMoney: non-finite value ${String(raw)}`);
  return n as Money;
}

/**
 * The server-computed per-minute P&L. Rendered, never recomputed (PLAN.md Part 3 §1).
 *
 * `netPerMin` is **profit** — takings less the market value of the ingredients sold — and it is
 * the figure the player is shown, because it is the one that should drive a pricing decision.
 * `cashPerMin` is how fast the balance itself moves, which is takings: the stock was already
 * paid for at the market. They differ between a restock and the batch selling out, and only the
 * second is any use for animating a number between polls.
 */
export interface Income {
  readonly grossPerMin: Money;
  readonly ingredientCostPerMin: Money;
  readonly wagePerMin: Money;
  readonly rentPerMin: Money;
  readonly rentIncomePerMin: Money;
  readonly netPerMin: Money;
  readonly cashPerMin: Money;
  readonly interestPerMin: Money;
  readonly dayRevenue: Money;
}

/** Something installed in a building: a shelf, a checkout, a storage rack, or stairs. */
export interface Fixture {
  readonly id: string;
  readonly kind: 'shelf' | 'checkout' | 'storage' | 'stairs';
  readonly floor: number;
  readonly slot: number;
  /** What a shelf sells; null on everything else, and on a shelf not yet stocked. */
  readonly itemId: string | null;
  readonly qty: number;
  readonly price: Money | null;
}

/** A building's bulk stock: what the racks can hold, and what is in them. */
export interface BuildingStorage {
  readonly capacity: number;
  readonly used: number;
  readonly items: Readonly<Record<string, number>>;
}

/** Goods on a truck, on their way to a building. */
export interface Delivery {
  readonly itemId: string;
  readonly qty: number;
  readonly arrivesAt: string;
}

/** The server's live read of a building trading as a shop. */
export interface ShopRate {
  readonly open: boolean;
  readonly customersPerMin: number;
  readonly revenuePerMin: Money;
  readonly shelves: ReadonlyArray<{
    readonly id: string;
    readonly item: string;
    readonly price: Money;
    readonly perMin: number;
  }>;
}

/**
 * A building the player can click on: its real OSM footprint, what it costs, who owns it and
 * what is installed inside. Every figure is the server's — the client never prices land, never
 * counts a floor as reachable, and never computes shop income.
 */
export interface Parcel {
  readonly id: string;
  readonly osmWayId: number | null;
  readonly landUse: string | null;
  readonly areaM2: number;
  readonly levels: number;
  /** Storeys, derived server-side from the building's real ingested height. */
  readonly floors: number;
  readonly roadClass: string | null;
  readonly price: Money;
  readonly lon: number;
  readonly lat: number;
  /** Exterior ring as `[lon, lat]` pairs, for the selection outline and the interior's shape. */
  readonly ring: ReadonlyArray<readonly [number, number]>;
  /** Bearing from the footprint centre to the nearest road, degrees clockwise from north. */
  readonly frontageDeg: number | null;
  readonly owned: boolean;
  readonly ownedByMe: boolean;
  readonly pricePaid: Money | null;
  /** What selling returns — server-computed. Null unless the parcel is the caller's to sell. */
  readonly resaleValue: Money | null;
  /** Floors you can actually walk to: ground plus one per consecutive staircase. */
  readonly reachableFloors: number;
  /** Fixture positions one floor offers, from its real area. */
  readonly floorSlots: number;
  readonly stairsCost: Money;
  readonly fixtures: readonly Fixture[];
  readonly storage: BuildingStorage | null;
  readonly deliveries: readonly Delivery[];
  readonly shop: ShopRate | null;
}

/** One of the player's buildings, as carried in the poll. */
export interface Holding {
  readonly parcelId: string;
  readonly osmWayId: number | null;
  readonly areaM2: number;
  readonly price: Money;
  readonly lon: number;
  readonly lat: number;
  readonly floors: number;
  readonly fixtures: number;
  readonly shopOpen: boolean;
  readonly shopPerMin: Money;
}

/** The player's land, summarised. `holdings` is capped server-side; `owned` is the true count. */
export interface LandState {
  readonly owned: number;
  readonly built: number;
  readonly portfolioValue: Money;
  readonly rentPerMin: Money;
  readonly holdings: readonly Holding[];
}

/** Where a cart's appeal comes from — shown as chips under the total. */
export interface AppealBreakdown {
  readonly design: number;
  readonly cleanliness: number;
  readonly menu: number;
  readonly tuneup: number;
  readonly total: number;
}

/** One recipe line, with how much the cart currently holds. */
export interface RecipeLine {
  readonly itemId: string;
  readonly name: string;
  readonly qty: number;
  readonly have: number;
}

/** A cuisine the player may choose when placing their first cart. */
export interface CuisineChoice {
  readonly cuisine: string;
  readonly name: string;
  readonly bundle: string;
  readonly basePrice: Money;
  readonly unitCost: Money;
  readonly recipe: ReadonlyArray<{ readonly itemId: string; readonly qty: number }>;
}

/**
 * A food cart. `pricePerDog` is the one number the player sets directly; everything alongside
 * it is server-computed and read-only here — `customersPerMin`, `optimalPrice`, `appeal` and
 * `cleanliness` are outputs of the demand model (PLAN.md Part 3 §6), not values the client may
 * derive. Before placement most of them are null or zero: the cart is still in the bag.
 */
export interface Cart {
  readonly id: string;
  /** False while the cart is still in the player's bag, with no pitch and no cuisine. */
  readonly placed: boolean;
  readonly name: string;
  readonly cuisine: string | null;
  readonly lon: number | null;
  readonly lat: number | null;
  readonly pricePerDog: Money;
  readonly effectiveDogPrice: Money;
  readonly unitCost: Money;
  readonly optimalPrice: Money;
  /** Portions ready to hand over right now. Only these can be sold. */
  readonly readyServings: number;
  /** Alias of readyServings, kept for the gauge that predates the prep step. */
  readonly preppedDogs: number;
  /** How many servings the ingredients on board *could* make, once prepped. */
  readonly servingsAvailable: number;
  readonly capacity: number;
  readonly prepCap: number;
  readonly cleanliness: number;
  readonly cookMastery: number;
  readonly soldToday: number;
  readonly customersPerMin: number;
  readonly appeal: number;
  readonly appealBreakdown: AppealBreakdown;
  /**
   * Keyed by commodity id — `{ FRANKS: 150, BUNS: 30 }`. Deliberately a different shape from
   * the player's top-level `inventory` array; both read the same table, and the server emits
   * each shape for its own call site (PLAN.md Part 3 §2).
   */
  readonly inventory: Readonly<Record<string, number>>;
  /** What one serving consumes, and how much is on board. Drives prep and restock. */
  readonly recipe: readonly RecipeLine[];
}

/** One commodity's live market row. Every number here is server-computed. */
export interface MarketEntry {
  readonly key: string;
  readonly name: string;
  readonly category: string;
  readonly price: Money;
  /** Percent change against 24h ago (or the oldest tick we have). Sign drives the ticker. */
  readonly trendPct: number;
  readonly spread: number;
  readonly last: Money;
  readonly volume: number;
}

/** A row of the static catalog. Fetched once at boot, not on every poll. */
export interface CatalogItem {
  readonly id: string;
  readonly name: string;
  readonly category: string;
  readonly stackMax: number;
  readonly slot: string | null;
  readonly bonuses: Readonly<Record<string, unknown>>;
  readonly price: Money;
}

export interface InventoryEntry {
  readonly itemId: string;
  readonly qty: number;
}

/**
 * Phase 2's slice of the state blob. The remaining observed keys — market, orders, delivery,
 * inventory, workers, vehicles, lanes, sites, landHoldings, leases, offlineReport and the rest
 * (PLAN.md Part 1 §4) — arrive with the phases that implement them; the server already returns
 * a stable envelope, so adding them is additive on both sides.
 */
export interface GameState {
  readonly playerId: string;
  /** Server clock at the moment of the read. Interpolation anchors to this, not to Date.now(). */
  readonly serverTime: string;
  readonly cash: Money;
  readonly bank: Money;
  readonly netWorth: Money;
  readonly loyalty: number;
  readonly day: number;
  readonly run: number;
  readonly income: Income;
  /** The player's first cart — what the HUD and cart window default to. Null before bootstrap. */
  readonly cart: Cart | null;
  readonly carts: readonly Cart[];
  /** The player's own stores, array form (the cart carries its own, keyed form). */
  readonly inventory: readonly InventoryEntry[];
  /** All commodities, live. Drives the ticker. */
  readonly market: readonly MarketEntry[];
  /** The nine cuisines offered when placing a cart. */
  readonly cuisines: readonly CuisineChoice[];
  /** The player's land: what they own, what is built, and what it earns. */
  readonly land: LandState;
}

/** Live-connection state, surfaced by ui/connStatus.ts. */
export type ConnPhase = 'idle' | 'connecting' | 'online' | 'unconfigured' | 'error';

export interface ConnState {
  readonly phase: ConnPhase;
  readonly detail: string;
  /** Round-trip time of the probe query, ms. Null unless `phase === 'online'`. */
  readonly latencyMs: number | null;
}
